import type {
  Plugin,
  PluginType,
  PluginCapabilities,
  Tool,
  Resource,
  ToolContext,
} from "./interfaces";
import type { MessageHandler, MessageSender } from "@brains/messaging-service";
import type { IShell } from "./interfaces";
import { ToolContextRoutingSchema } from "./interfaces";
import {
  getErrorMessage,
  Logger,
  type ProgressNotification,
  ProgressReporter,
} from "@brains/utils";
import type { UserPermissionLevel } from "@brains/templates";
import { z } from "@brains/utils";

// Message schemas for validation
const toolExecuteRequestSchema = z.object({
  toolName: z.string(),
  args: z.unknown(),
  progressToken: z.union([z.string(), z.number()]).optional(),
  hasProgress: z.boolean().optional(),
  // Reuse the shared routing metadata schema
  ...ToolContextRoutingSchema.shape,
});

const resourceGetRequestSchema = z.object({
  resourceUri: z.string(),
});

// Core context interface - minimal requirements for all plugins
export interface CoreContext {
  pluginId: string;
  logger: Logger;
  messaging: {
    send: MessageSender;
    subscribe: <T = unknown, R = unknown>(
      channel: string,
      handler: MessageHandler<T, R>,
    ) => () => void;
  };
}

/**
 * Base abstract class for plugins that provides common functionality
 */
export abstract class BasePlugin<
  TConfig = unknown,
  TContext extends CoreContext = CoreContext,
> implements Plugin {
  public readonly id: string;
  public readonly version: string;
  public readonly description: string;
  public readonly packageName: string;
  public abstract readonly type: PluginType;
  protected config: TConfig;
  protected context?: TContext;

  /**
   * Get the logger - uses context logger if available, creates temp logger otherwise
   */
  protected get logger(): Logger {
    return this.context?.logger ?? Logger.createFresh({ context: this.id });
  }

  constructor(
    id: string,
    packageJson: { name: string; version: string; description?: string },
    partialConfig: Partial<TConfig>,
    configSchema: z.ZodTypeAny,
  ) {
    this.id = id;
    this.packageName = packageJson.name;
    this.version = packageJson.version;
    this.description = packageJson.description ?? `${packageJson.name} plugin`;

    // Let Zod schema handle defaults during parsing
    this.config = configSchema.parse(partialConfig);
  }

  /**
   * Register the plugin - must be overridden by typed base classes
   * to create appropriate context from shell
   */
  abstract register(shell: IShell): Promise<PluginCapabilities>;

  /**
   * Set up message handlers for tool and resource execution
   */
  protected setupMessageHandlers(context: TContext): void {
    // Subscribe to tool execution requests for this specific plugin
    context.messaging.subscribe(
      `plugin:${this.id}:tool:execute`,
      async (message) => {
        try {
          // Validate and parse the message payload
          const {
            toolName,
            args,
            progressToken,
            hasProgress,
            interfaceType,
            userId,
            channelId,
            userPermissionLevel,
          } = toolExecuteRequestSchema.parse(message.payload);

          const tools = await this.getTools();
          const tool = tools.find((t) => t.name === toolName);

          if (!tool) {
            return {
              success: false,
              error: `Tool not found: ${toolName}`,
            };
          }

          // Create context with routing metadata and optional progress callback
          const toolContext: ToolContext = {
            interfaceType,
            userId,
            ...(channelId && { channelId }),
            ...(userPermissionLevel && { userPermissionLevel }),
            ...(hasProgress &&
              progressToken !== undefined && {
                progressToken,
                sendProgress: async (
                  notification: ProgressNotification,
                ): Promise<void> => {
                  // Send progress notification back through message bus
                  await context.messaging.send({
                    type: `plugin:${this.id}:progress`,
                    payload: {
                      progressToken,
                      notification,
                    },
                  });
                },
              }),
          };

          // Response shape is validated at the consumer boundary
          // (MCPService registry wrapper / mcp-registration protocol path).
          const result = await tool.handler(args, toolContext);
          return {
            success: true,
            data: result,
          };
        } catch (error) {
          if (error instanceof z.ZodError) {
            return {
              success: false,
              error: "Invalid tool execution request format",
            };
          }
          this.logger.error("Tool execution error", error);
          return {
            success: false,
            error: getErrorMessage(error),
          };
        }
      },
    );

    // Subscribe to resource get requests for this specific plugin
    context.messaging.subscribe(
      `plugin:${this.id}:resource:get`,
      async (message) => {
        try {
          // Validate and parse the message payload
          const { resourceUri } = resourceGetRequestSchema.parse(
            message.payload,
          );

          const resources = await this.getResources();
          const resource = resources.find((r) => r.uri === resourceUri);

          if (!resource) {
            return {
              success: false,
              error: `Resource not found: ${resourceUri}`,
            };
          }

          // Get the resource
          const result = await resource.handler();
          return {
            success: true,
            data: result,
          };
        } catch (error) {
          if (error instanceof z.ZodError) {
            return {
              success: false,
              error: "Invalid resource get request format",
            };
          }
          this.logger.error("Resource fetch error", error);
          return {
            success: false,
            error: getErrorMessage(error),
          };
        }
      },
    );
  }

  /**
   * Lifecycle hook called during registration.
   * Override this for capability registration and subscriptions that must exist
   * before the system-wide "all plugins registered" signal.
   */
  protected async onRegister(_context: TContext): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Lifecycle hook called after all plugins have registered and the shell has
   * completed ready-state preparation.
   * Override this for work that reads identity/profile or depends on other
   * plugins having completed registration.
   */
  protected async onReady(_context: TContext): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Dispatch the ready lifecycle hook.
   */
  public async ready(): Promise<void> {
    await this.onReady(this.getContext());
  }

  /**
   * Build plugin capabilities from overridable capability hooks.
   */
  protected async getCapabilities(): Promise<PluginCapabilities> {
    const instructions = await this.getInstructions();
    return {
      tools: await this.getTools(),
      resources: await this.getResources(),
      ...(instructions && { instructions }),
    };
  }

  /**
   * Get the tools provided by this plugin
   * Override this to provide plugin-specific tools
   */
  protected async getTools(): Promise<Tool[]> {
    return [];
  }

  /**
   * Get the resources provided by this plugin
   * Override this to provide plugin-specific resources
   */
  protected async getResources(): Promise<Resource[]> {
    return [];
  }

  /**
   * Get behavioral instructions for the agent's system prompt
   * Override this to provide plugin-specific agent guidance
   */
  protected async getInstructions(): Promise<string | undefined> {
    return undefined;
  }

  /**
   * Optional shutdown method for cleanup
   * Override this if your plugin needs cleanup
   */
  async shutdown?(): Promise<void> {
    await this.onShutdown();
  }

  /**
   * Lifecycle hook called during shutdown
   * Override this to perform cleanup
   */
  protected async onShutdown(): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Get the plugin context
   */
  protected getContext(): TContext {
    if (!this.context) {
      throw new Error(`Plugin ${this.id}: Plugin not registered yet`);
    }
    return this.context;
  }

  /**
   * Determine the permission level for a user in this plugin's context
   * Must be implemented by plugins that handle user permissions
   * Default implementation returns 'public' for safety
   */
  public determineUserPermissionLevel(_userId: string): UserPermissionLevel {
    return "public";
  }

  /**
   * Create a progress bridge that converts job queue ProgressReporter to MCP progress events
   * Used by job-based tools to report progress via MCP progressToken
   */
  protected createProgressBridge(
    progressToken?: string | number,
  ): ProgressReporter | undefined {
    if (!progressToken) return undefined;

    const context = this.context;
    if (!context) return undefined;

    const pluginId = this.id;
    return ProgressReporter.from(async (notification: ProgressNotification) => {
      await context.messaging.send({
        type: `plugin:${pluginId}:progress`,
        payload: {
          progressToken,
          notification: {
            progress: notification.progress,
            total: notification.total,
            message: notification.message,
          },
        },
      });
    });
  }
}
